import { PREPARATION_STATES } from '../domain/index.js';
import { isNumber, isObject, refuse } from './decode.js';
const isString = (value) => typeof value === 'string';
const fields = (value, where, required, optional = {}) => {
    if (!isObject(value))
        return refuse(`${where} is not an object`);
    for (const key of Object.keys(value)) {
        if (!(key in required) && !(key in optional))
            refuse(`${where} has an unknown field ${key}`);
    }
    for (const [key, check] of Object.entries(required)) {
        if (!(key in value))
            refuse(`${where} has no ${key}`);
        check(value[key], `${where}.${key}`);
    }
    for (const [key, check] of Object.entries(optional)) {
        if (value[key] !== undefined)
            check(value[key], `${where}.${key}`);
    }
    return value;
};
const string = (value, where) => {
    if (!isString(value))
        refuse(`${where} is not a string`);
};
const number = (value, where) => {
    if (!isNumber(value))
        refuse(`${where} is not a number`);
};
const oneOf = (...allowed) => (value, where) => {
    if (!isString(value) || !allowed.includes(value)) {
        refuse(`${where} must be one of ${allowed.join(', ')}`);
    }
};
const list = (item) => (value, where) => {
    if (!Array.isArray(value))
        return refuse(`${where} is not a list`);
    value.forEach((v, i) => item(v, `${where}[${i}]`));
};
/** A discriminated union: the discriminant picks the fields to check. */
const union = (key, variants) => (value, where) => {
    if (!isObject(value))
        return refuse(`${where} is not an object`);
    const tag = value[key];
    const variant = isString(tag) ? variants[tag] : undefined;
    if (!variant || !Object.hasOwn(variants, tag)) {
        return refuse(`${where}.${key} must be one of ${Object.keys(variants).join(', ')}`);
    }
    variant(value, where);
};
const trueOnly = (value, where) => {
    if (value !== true)
        refuse(`${where} is true or absent`);
};
const range = (value, where) => {
    if (!Array.isArray(value) || value.length !== 2 || !value.every(isNumber)) {
        refuse(`${where} must be two numbers`);
    }
};
const rangeBound = union('kind', {
    finite: (v, w) => fields(v, w, { kind: string, value: number }),
    beyondHorizon: (v, w) => fields(v, w, { kind: string, horizonS: number }),
});
const modelRef = (v, w) => fields(v, w, { id: string, version: string });
/** A portable estimate range: two bounds (values-and-units.md). */
export const estimateRange = (value, where) => {
    if (!Array.isArray(value) || value.length !== 2)
        return refuse(`${where} must be two bounds`);
    value.forEach((bound, i) => rangeBound(bound, `${where}[${i}]`));
};
/** The estimate that came with an applied suggestion: always `ok`. */
const acceptedEstimate = (v, w) => fields(v, w, { status: oneOf('ok'), value: number, model: modelRef, assumptions: list(string) }, { range: estimateRange, extrapolations: list(string), calibration: modelRef });
/** Provenance of an applied model suggestion (fermentation.md). */
const accepted = (v, w) => fields(v, w, { estimate: acceptedEstimate, appliedValue: number, model: modelRef });
const content = union('status', {
    known: (v, w) => fields(v, w, { status: string, valuePct: number }, { source: string }),
    estimated: (v, w) => fields(v, w, { status: string, valuePct: number }, { rangePct: range, source: string }),
    unknown: (v, w) => fields(v, w, { status: string }),
});
const flour = (v, w) => fields(v, w, { id: string, sharePct: number });
const ingredient = (v, w) => fields(v, w, {
    id: string,
    kind: oneOf('water', 'other'),
    bakerPct: number,
    role: oneOf('dough', 'topping', 'glaze'),
}, {
    water: content,
    fat: content,
    sugar: content,
    yeast: (y, yw) => fields(y, yw, { form: oneOf('fresh', 'active-dry', 'instant') }),
    accepted,
    entry: (e, ew) => fields(e, ew, { id: string, version: number }, { customized: trueOnly }),
    overrides: list(oneOf('kind', 'water', 'fat', 'sugar', 'yeast')),
    preparation: (p, pw) => fields(p, pw, { state: oneOf(...PREPARATION_STATES) }, { note: string }),
});
const component = (v, w) => fields(v, w, {
    id: string,
    kind: oneOf('preferment', 'scald', 'soaker', 'custom'),
    flourPct: number,
    flourBlend: list((p, pw) => fields(p, pw, { flour: string, sharePct: number })),
    hydrationPct: number,
}, {
    waterLossPct: number,
    allocations: list((a, aw) => fields(a, aw, { ingredient: string, bakerPct: number }, { accepted })),
});
const anchor = union('kind', {
    flourTotal: (v, w) => fields(v, w, { kind: string, grams: number }),
    doughWeight: (v, w) => fields(v, w, { kind: string, grams: number }),
    pieces: (v, w) => fields(v, w, { kind: string, count: number, gramsEach: number }),
});
const endCriterion = union('kind', {
    duration: (v, w) => fields(v, w, { kind: string }),
    observation: (v, w) => fields(v, w, { kind: string }, { description: string }),
    userDecision: (v, w) => fields(v, w, { kind: string }),
});
const incorporation = union('item', {
    flour: (v, w) => fields(v, w, { item: string, id: string, sharePct: number }),
    ingredient: (v, w) => fields(v, w, { item: string, id: string, sharePct: number }),
    component: (v, w) => fields(v, w, { item: string, id: string, sharePct: number }),
    water: (v, w) => fields(v, w, { item: string, sharePct: number }),
});
/** Techniques (process.md): method, end check and fold style stay open codes. */
const technique = (v, w) => fields(v, w, {}, {
    method: string,
    phases: list((p, pw) => fields(p, pw, { speed: oneOf('low', 'medium', 'high'), durationS: number })),
    folds: (f, fw) => fields(f, fw, { count: number, intervalS: number }, { style: string }),
    endCheck: string,
});
/** A preparation outcome (bake-session.md); its rules are checked by validateProcess. */
const outcome = (v, w) => fields(v, w, { result: oneOf('cooked', 'dough', 'cookedAndDough') }, { dough: oneOf('frozen', 'refrigerated', 'given', 'sold', 'other'), pieces: number });
const equipmentRef = (v, w) => fields(v, w, { id: string, version: number });
const preheat = oneOf('hot', 'cold', 'unknown');
/** The settings of a step (equipment.md); their rules are checked by validateProcess. */
export const stepEquipment = (v, w) => fields(v, w, {}, {
    appliance: oneOf('oven', 'stovetop', 'grill', 'steamer', 'fryer', 'proofer', 'other'),
    profile: equipmentRef,
    mode: oneOf('bottom', 'topBottom', 'convection', 'trueConvection', 'convectionRoast', 'broil', 'convectionBroil', 'steam', 'proof', 'custom'),
    heat: list(oneOf('top', 'bottom', 'rear', 'fan')),
    nativeMode: string,
    setpointC: number,
    level: (l, lw) => fields(l, lw, { value: number }, { of: number }),
    fan: oneOf('on', 'off', 'unknown'),
    steam: (s, sw) => fields(s, sw, { source: oneOf('function', 'manual', 'none') }, { detail: string }),
    support: (s, sw) => fields(s, sw, { item: equipmentRef }),
    vessel: (s, sw) => fields(s, sw, {
        item: equipmentRef,
        cover: oneOf('open', 'covered', 'closed', 'unknown'),
    }),
    preheated: (p, pw) => fields(p, pw, {}, { appliance: preheat, support: preheat, vessel: preheat }),
    rack: string,
    load: (l, lw) => fields(l, lw, {}, { pieces: number, massG: number }),
});
const step = (v, w) => fields(v, w, {
    id: string,
    operation: string,
    dependsOn: list(string),
    schedule: oneOf('asap', 'alap'),
}, {
    label: string,
    plannedDurationS: number,
    endCriterion,
    conditions: (c, cw) => fields(c, cw, {}, { location: string, temperatureC: number, doughTemperatureC: number, frictionC: number }),
    incorporations: list(incorporation),
    accepted,
    technique,
    durationMode: oneOf('manual', 'model'),
    equipment: stepEquipment,
});
/** Refuses (throws `Refused`) unless the value has exactly the shape of a formula. */
export const decodeFormula = (value, where) => {
    fields(value, where, {
        flours: list(flour),
        ingredients: list(ingredient),
        components: list(component),
        anchor,
    }, {
        waterTarget: (v, w) => fields(v, w, { totalWaterPct: number, absorbInto: string }),
    });
    return value;
};
/** Refuses (throws `Refused`) unless the value has exactly the shape of a process. */
export const decodeProcess = (value, where) => {
    fields(value, where, { steps: list(step) }, { readiness: modelRef, intendedOutcome: outcome });
    return value;
};
//# sourceMappingURL=documents.js.map